import { useCallback, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"

import { MIN_DRAG_PIXELS } from "helpers/utils/constants"
import {
  clampToPlotX,
  computeZoomRange,
  isInsideDrawingArea,
  isOutsideVertically,
  pixelToTimestamp,
} from "./helpers"
import type { PlotBounds, SelectionBand, SvgPoint } from "./helpers"

interface UseDragZoomOptions {
  svgRef: React.RefObject<SVGSVGElement | null>
  drawingArea: PlotBounds
  getSvgPoint: (e: PointerEvent) => SvgPoint | null
  commitRange: (start: DateTime, end: DateTime) => void
  xDomainMin: number
  xDomainMax: number
}

interface UseDragZoomReturn {
  /** True while a drag is live, which is what decides whose band gets drawn. */
  isDragging: boolean
  /** The band to preview, or null when the drag is too short to be applied. */
  selection: SelectionBand | null
  onPointerDown: (e: PointerEvent) => void
  onPointerMove: (e: PointerEvent) => void
  onPointerUp: (e: PointerEvent) => void
  onPointerCancel: (e: PointerEvent) => void
  /** Drop the drag without applying anything. */
  cancel: () => void
}

/**
 * The mouse/pen half of the zoom: press inside the plot, drag horizontally to
 * select a range, release to apply it. Leaving the plot vertically aborts.
 *
 * The pointer is captured on pointerdown so move/up keep arriving once the
 * cursor leaves the SVG — which is also why the capture has to be released on
 * every exit, unmount included: the SVG belongs to the parent chart.
 */
const useDragZoom = ({
  svgRef,
  drawingArea,
  getSvgPoint,
  commitRange,
  xDomainMin,
  xDomainMax,
}: UseDragZoomOptions): UseDragZoomReturn => {
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<{ x: number; value: Date | null }>({
    x: 0,
    value: null,
  })
  const [dragCurrent, setDragCurrent] = useState<{ x: number; value: Date | null }>({
    x: 0,
    value: null,
  })

  // The pointer that owns the drag, so its capture can be released on
  // pointerup/cancel and on unmount.
  const activePointerIdRef = useRef<number | null>(null)

  const pixelToAxisValue = useCallback(
    (pixelX: number): Date | null => {
      const timestamp = pixelToTimestamp({
        x: pixelX,
        drawingArea,
        domainMin: xDomainMin,
        domainMax: xDomainMax,
      })

      return timestamp === null ? null : new Date(timestamp)
    },
    [drawingArea, xDomainMin, xDomainMax],
  )

  // Abort the drag without applying any zoom: release the capture and reset.
  const cancel = useCallback(() => {
    const svg = svgRef.current
    const activePointerId = activePointerIdRef.current
    if (svg && activePointerId !== null && svg.hasPointerCapture(activePointerId)) {
      svg.releasePointerCapture(activePointerId)
    }
    activePointerIdRef.current = null
    setIsDragging(false)
  }, [svgRef])

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      // A drag is already in flight; any further pointer is not part of it.
      if (activePointerIdRef.current !== null) return

      const svg = svgRef.current
      if (!svg) return

      const point = getSvgPoint(e)
      if (!point) return
      if (!isInsideDrawingArea(point, drawingArea)) return

      // Capture the pointer so move/up keep arriving once the cursor leaves the SVG.
      svg.setPointerCapture(e.pointerId)
      activePointerIdRef.current = e.pointerId

      const axisValue = pixelToAxisValue(point.x)
      setDragStart({ x: point.x, value: axisValue })
      setDragCurrent({ x: point.x, value: axisValue })
      setIsDragging(true)
    },
    [svgRef, getSvgPoint, drawingArea, pixelToAxisValue],
  )

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return

      // Only the pointer that started the drag drives it; ignore any others.
      if (e.pointerId !== activePointerIdRef.current) return

      const point = getSvgPoint(e)
      if (!point) return

      // Leaving the plot vertically aborts the zoom: an escape hatch mid-drag.
      if (isOutsideVertically(point.y, drawingArea)) {
        cancel()
        return
      }

      const x = clampToPlotX(point.x, drawingArea)
      setDragCurrent({ x, value: pixelToAxisValue(x) })
    },
    [isDragging, cancel, getSvgPoint, pixelToAxisValue, drawingArea],
  )

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return

      // Only the pointer that started the drag can finish it.
      if (e.pointerId !== activePointerIdRef.current) return

      // Browsers auto-release on pointerup, but the SVG outlives us: be explicit.
      const svg = svgRef.current
      if (svg?.hasPointerCapture(e.pointerId)) {
        svg.releasePointerCapture(e.pointerId)
      }
      activePointerIdRef.current = null

      setIsDragging(false)

      if (dragStart.value && dragCurrent.value) {
        // Ignore very small drags - likely accidental or a double-click
        if (Math.abs(dragCurrent.x - dragStart.x) < MIN_DRAG_PIXELS) return

        const { start, end } = computeZoomRange(
          DateTime.fromJSDate(dragStart.value),
          DateTime.fromJSDate(dragCurrent.value),
        )
        commitRange(start, end)
      }
    },
    [isDragging, dragStart, dragCurrent, commitRange, svgRef],
  )

  // A cancel means the browser took the gesture over. Only the pointer that
  // owns the drag can end it: these listeners live on window, so a pointer
  // cancelled elsewhere on the page must not kill an active chart gesture.
  const onPointerCancel = useCallback(
    (e: PointerEvent) => {
      if (e.pointerId !== activePointerIdRef.current) return
      cancel()
    },
    [cancel],
  )

  // Release the capture if we unmount mid-drag. Read the SVG node now, while it
  // is still ours to reach.
  useEffect(() => {
    const svg = svgRef.current
    return () => {
      const activePointerId = activePointerIdRef.current
      if (svg && activePointerId !== null && svg.hasPointerCapture(activePointerId)) {
        svg.releasePointerCapture(activePointerId)
      }
      activePointerIdRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const width = Math.abs(dragCurrent.x - dragStart.x)

  return {
    isDragging,
    // Below the threshold the drag is not applied, so a band would promise a
    // zoom that never happens.
    selection:
      isDragging && width >= MIN_DRAG_PIXELS
        ? { x: Math.min(dragStart.x, dragCurrent.x), width }
        : null,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    cancel,
  }
}

export default useDragZoom
